import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listAgreements } from '../services/agreementService';
import { getDraftByAgreementId } from '../services/agreementDraftService';
import { listHospitals } from '../services/hospitalService';
import type { AgreementDetail, Hospital } from '../types';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export default function AgreementListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [agreements, setAgreements] = useState<AgreementDetail[]>([]);
  const [filteredAgreements, setFilteredAgreements] = useState<AgreementDetail[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [selectedHospitalUuid, setSelectedHospitalUuid] = useState('');
  const [hospitalSearch, setHospitalSearch] = useState('');
  const [hospitalDropdownOpen, setHospitalDropdownOpen] = useState(false);
  const hospitalDropdownRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftMap, setDraftMap] = useState<Record<number, number>>({}); // agreementId -> draftId

  const isAdmin = user?.role === 'ADMIN';

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (hospitalDropdownRef.current && !hospitalDropdownRef.current.contains(e.target as Node)) {
        setHospitalDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load hospitals for the filter dropdown
  useEffect(() => {
    async function loadHospitals() {
      try {
        const result = await listHospitals({ limit: 1000 });
        setHospitals(result.hospitals);
      } catch {
        // Silently fail - filter just won't be populated
      }
    }
    loadHospitals();
  }, []);

  const fetchAgreements = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listAgreements({});
      setAgreements(result.agreements);
      
      // Check for drafts for each agreement
      if (isAdmin) {
        const drafts: Record<number, number> = {};
        await Promise.all(
          result.agreements.map(async (agreement) => {
            try {
              const draft = await getDraftByAgreementId(agreement.agreementId);
              if (draft) {
                drafts[agreement.agreementId] = draft.draftId;
              }
            } catch {
              // Ignore errors for individual draft checks
            }
          })
        );
        setDraftMap(drafts);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load agreements';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  // Filter agreements based on selected hospital
  useEffect(() => {
    if (!selectedHospitalUuid) {
      setFilteredAgreements(agreements);
    } else {
      setFilteredAgreements(
        agreements.filter((agreement) => agreement.hospitalUuid === selectedHospitalUuid)
      );
    }
  }, [agreements, selectedHospitalUuid]);

  const handleSearchChange = (value: string) => {
    setHospitalSearch(value);
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="agreement-list-page">
      <div className="page-header">
        <h2>Agreement Management</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isAdmin && (
            <>
              <Button onClick={() => navigate('/agreements/drafts')} variant="secondary">
                Drafts
              </Button>
              <Button onClick={() => navigate('/agreements/create')}>
                Create Agreement
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="filters-row" style={{ marginBottom: '1.5rem' }}>
        <div style={{ width: '100%', maxWidth: '600px' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            Filter by Hospital
          </label>
          <div ref={hospitalDropdownRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setHospitalDropdownOpen(!hospitalDropdownOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontSize: '0.9rem',
                color: selectedHospitalUuid ? '#111827' : '#9ca3af',
              }}
            >
              <span style={{ flex: 1 }}>
                {selectedHospitalUuid
                  ? hospitals.find((h) => h.hospitalUuid === selectedHospitalUuid)
                    ? `${hospitals.find((h) => h.hospitalUuid === selectedHospitalUuid)!.hospitalName} (${hospitals.find((h) => h.hospitalUuid === selectedHospitalUuid)!.hospitalCode})`
                    : 'All Hospitals'
                  : 'All Hospitals'}
              </span>
              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>▼</span>
            </div>
            {hospitalDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 50,
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  marginTop: '2px',
                  maxHeight: '260px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ padding: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="Search hospitals..."
                    value={hospitalSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '0.4rem 0.5rem',
                      fontSize: '0.85rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                    aria-label="Search hospitals"
                  />
                </div>
                <div style={{ overflowY: 'auto', maxHeight: '200px' }}>
                  <div
                    onClick={() => {
                      setSelectedHospitalUuid('');
                      setHospitalDropdownOpen(false);
                      setHospitalSearch('');
                    }}
                    style={{
                      padding: '0.5rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      backgroundColor: !selectedHospitalUuid ? '#eff6ff' : 'transparent',
                      borderBottom: '1px solid #f3f4f6',
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { (e.target as HTMLDivElement).style.backgroundColor = '#f3f4f6'; }}
                    onMouseLeave={(e) => { (e.target as HTMLDivElement).style.backgroundColor = !selectedHospitalUuid ? '#eff6ff' : 'transparent'; }}
                  >
                    All Hospitals
                  </div>
                  {hospitals
                    .filter((h) => {
                      const term = hospitalSearch.toLowerCase();
                      return (
                        h.hospitalName.toLowerCase().includes(term) ||
                        h.hospitalCode.toLowerCase().includes(term)
                      );
                    })
                    .map((h) => (
                      <div
                        key={h.hospitalUuid}
                        onClick={() => {
                          setSelectedHospitalUuid(h.hospitalUuid);
                          setHospitalDropdownOpen(false);
                          setHospitalSearch('');
                        }}
                        style={{
                          padding: '0.5rem 0.75rem',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          backgroundColor: h.hospitalUuid === selectedHospitalUuid ? '#eff6ff' : 'transparent',
                          borderBottom: '1px solid #f3f4f6',
                        }}
                        onMouseEnter={(e) => { (e.target as HTMLDivElement).style.backgroundColor = '#f3f4f6'; }}
                        onMouseLeave={(e) => { (e.target as HTMLDivElement).style.backgroundColor = h.hospitalUuid === selectedHospitalUuid ? '#eff6ff' : 'transparent'; }}
                      >
                        {h.hospitalName} ({h.hospitalCode})
                      </div>
                    ))}
                  {hospitals.filter((h) => {
                    const term = hospitalSearch.toLowerCase();
                    return h.hospitalName.toLowerCase().includes(term) || h.hospitalCode.toLowerCase().includes(term);
                  }).length === 0 && (
                    <div style={{ padding: '0.5rem 0.75rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                      No hospitals found.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <LoadingSpinner message="Loading agreements..." />
      ) : (
        <table className="data-table" role="table">
          <thead>
            <tr>
              <th scope="col">Hospital Name</th>
              <th scope="col">Valid From</th>
              <th scope="col">Valid Till</th>
              <th scope="col">Version</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAgreements.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-row">
                  {selectedHospitalUuid ? 'No agreements found for selected hospital' : 'No agreements found.'}
                </td>
              </tr>
            ) : (
              filteredAgreements.map((agreement) => (
                <tr key={agreement.agreementId}>
                  <td>{agreement.hospitalName || agreement.hospitalUuid}</td>
                  <td>{formatDate(agreement.validFrom)}</td>
                  <td>{formatDate(agreement.validTill)}</td>
                  <td>{agreement.version}</td>
                  <td className="actions-cell">
                    {isAdmin && draftMap[agreement.agreementId] && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/agreements/create?draftId=${draftMap[agreement.agreementId]}`)}
                        style={{ color: '#f59e0b', fontWeight: 500 }}
                      >
                        Draft
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/agreements/${agreement.agreementId}/edit`)}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/agreements/${agreement.agreementId}/versions`)}
                    >
                      Versions
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/agreements/${agreement.agreementId}/documents`)}
                    >
                      Documents
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
